import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import Swal from 'sweetalert2';
import { isPlatformAdmin as currentUserIsPlatformAdmin } from '../../../utils/platformRole';
import './UserManagement.css';

const ORG_ROLES = [
  { value: 'MEMBER',        labelKey: 'roleMember',       color: '#475569', bg: '#f1f5f9' },
  { value: 'ORG_ADMIN',     labelKey: 'roleOrgAdmin',     color: '#1e40af', bg: '#dbeafe' },
  { value: 'AUDITOR',       labelKey: 'roleAuditor',      color: '#065f46', bg: '#d1fae5' },
  { value: 'VIEWER',        labelKey: 'roleViewer',       color: '#92400e', bg: '#fef3c7' },
  { value: 'CISO_RESELLER', labelKey: 'roleCisoReseller', color: '#6d28d9', bg: '#ede9fe' },
  { value: 'CPA_ADMIN',     labelKey: 'roleCpaAdmin',     color: '#9d174d', bg: '#fce7f3' },
  { value: 'CPA_AUDITOR',   labelKey: 'roleCpaAuditor',   color: '#1e40af', bg: '#e0f2fe' },
];

const PARTNER_ORG_ROLES = ['CISO_RESELLER', 'CPA_ADMIN', 'CPA_AUDITOR'];

function OrgRoleBadge({ role }) {
  const { t } = useTranslation();
  const def = ORG_ROLES.find(r => r.value === role);
  const color = def ? def.color : '#475569';
  const bg = def ? def.bg : '#f1f5f9';
  const label = def ? t(`UserManagement.${def.labelKey}`) : (role || t('UserManagement.roleMember'));
  return (
    <span className="um-role-badge" style={{ color, background: bg }}>
      {label}
    </span>
  );
}

function SystemRoleBadge({ authorities = [] }) {
  const { t } = useTranslation();
  const isPlatformAdmin = authorities.some(a => a.name === 'ROLE_PLATFORM_ADMIN');
  const isPlatformUser  = authorities.some(a => a.name === 'ROLE_PLATFORM_USER');
  const isAdmin = authorities.some(a => a.name === 'ROLE_ADMIN');
  const label = isPlatformAdmin ? t('UserManagement.systemRolePlatformAdmin')
              : isPlatformUser  ? t('UserManagement.systemRolePlatformUser')
              : isAdmin         ? t('UserManagement.systemRoleAdmin')
              :                   t('UserManagement.systemRoleUser');
  const color = isPlatformAdmin ? '#7c3aed' : isPlatformUser ? '#0e7490' : isAdmin ? '#1e40af' : '#475569';
  const bg    = isPlatformAdmin ? '#ede9fe'  : isPlatformUser ? '#cffafe' : isAdmin ? '#dbeafe' : '#f1f5f9';
  return (
    <span className="um-sys-badge" style={{ color, background: bg }}>{label}</span>
  );
}

const CATAAM_ORG_ID = 100;

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phoneNumber: '',
  userName: '', password: '',
  orgMode: 'existing',   // 'existing' | 'new'
  organizationId: '',
  newOrgName: '',
  partnerId: '',
  orgRole: 'MEMBER',
  systemRole: 'ROLE_USER',
  isActive: true,
};

export default function UserManagement() {
  const { t } = useTranslation();
  const [users,   setUsers]   = useState([]);
  const [orgs,    setOrgs]    = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  const [resettingId, setResettingId] = useState(null);
  const [mfaResettingId, setMfaResettingId] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { name, tempPassword }
  const [deactivatingId, setDeactivatingId] = useState(null);
  const currentUserName = (localStorage.getItem('userName') || localStorage.getItem('username') || '').toLowerCase();

  const handleDeactivate = async (u) => {
    if (!window.confirm(t('UserManagement.confirmDeactivate', { name: `${u.firstName} ${u.lastName}` }))) return;
    setDeactivatingId(u.id);
    try {
      await axiosInstance.delete(`/users/${u.id}`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || t('UserManagement.errorDeactivate'));
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(t('UserManagement.confirmResetPassword', { name: `${u.firstName} ${u.lastName}` }))) return;
    setResettingId(u.id);
    try {
      const res = await axiosInstance.post(`/users/${u.id}/reset-password`);
      setResetResult({ name: `${u.firstName} ${u.lastName}`, tempPassword: res.data.tempPassword });
    } catch (e) {
      alert(e.response?.data?.error || t('UserManagement.errorResetPassword'));
    } finally {
      setResettingId(null);
    }
  };

  const handleResetMfa = async (u) => {
    if (!window.confirm(t('UserManagement.confirmResetMfa', { name: `${u.firstName} ${u.lastName}` }))) return;
    setMfaResettingId(u.id);
    try {
      await axiosInstance.post(`/users/${u.id}/reset-mfa`);
      alert(t('UserManagement.mfaResetSuccess', { name: `${u.firstName} ${u.lastName}` }));
    } catch (e) {
      alert(e.response?.data?.error || t('UserManagement.errorResetMfa'));
    } finally {
      setMfaResettingId(null);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes, partnerRes] = await Promise.allSettled([
        axiosInstance.get('/users', { params: { page: 0, size: 200 } }),
        axiosInstance.get('/organizations'),
        axiosInstance.get('/partner/admin/all'),
      ]);
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.map(u => ({
          ...u,
          organizationName: u.organization?.organizationName || '—',
        })));
      }
      if (orgsRes.status === 'fulfilled') setOrgs(orgsRes.value.data || []);
      if (partnerRes.status === 'fulfilled') setPartners(partnerRes.value.data || []);
    } catch (e) {
      console.error('Error loading user management data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setFieldErr({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setFieldErr({});
  };

  const setField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    setFieldErr(e => ({ ...e, [name]: undefined }));
  };

  // Only a full platform admin may grant platform roles (backend enforces this too).
  const canGrantPlatformRoles = currentUserIsPlatformAdmin();
  const isPlatformAdminForm = form.systemRole === 'ROLE_PLATFORM_ADMIN';
  const isPlatformUserForm  = form.systemRole === 'ROLE_PLATFORM_USER';
  // Both platform roles are TheMarkups staff attached to the Cataam org — same org handling.
  const isPlatformStaffForm = isPlatformAdminForm || isPlatformUserForm;

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = t('UserManagement.validationRequired');
    if (!form.lastName.trim())  errs.lastName  = t('UserManagement.validationRequired');
    if (!form.email.trim())     errs.email     = t('UserManagement.validationRequired');
    if (!form.userName.trim())  errs.userName  = t('UserManagement.validationRequired');
    if (!form.password.trim())  errs.password  = t('UserManagement.validationRequired');
    if (!isPlatformStaffForm) {
      if (form.orgMode === 'existing' && !form.organizationId) errs.organizationId = t('UserManagement.validationSelectOrganization');
      if (form.orgMode === 'new'      && !form.newOrgName.trim()) errs.newOrgName   = t('UserManagement.validationEnterOrganizationName');
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErr(errs); return; }
    setSaving(true);
    try {
      // platform_user gets ONLY ROLE_PLATFORM_USER — deliberately NOT ROLE_ADMIN, so it can never
      // satisfy the org-admin gate that guards user removal (OrgTeamService.requireOrgAdmin).
      const roles = isPlatformAdminForm ? ['ROLE_ADMIN', 'ROLE_PLATFORM_ADMIN']
                  : isPlatformUserForm  ? ['ROLE_PLATFORM_USER']
                  :                       [form.systemRole];
      const payload = {
        firstName:   form.firstName.trim(),
        lastName:    form.lastName.trim(),
        email:       form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        userName:    form.userName.trim(),
        password:    form.password,
        isActive:    form.isActive,
        roles,
        orgRole:     form.orgRole,
      };
      if (isPlatformStaffForm) {
        payload.organization = { id: CATAAM_ORG_ID };
      } else if (form.orgMode === 'existing') {
        payload.organization = { id: Number(form.organizationId) };
      } else {
        payload.newOrgName = form.newOrgName.trim();
        if (form.partnerId) payload.partnerId = Number(form.partnerId);
      }
      await axiosInstance.post('/users', payload);
      closeModal();
      fetchAll();
      Swal.fire({ title: t('UserManagement.userCreatedTitle'), text: t('UserManagement.userCreatedText', { name: `${form.firstName} ${form.lastName}` }), icon: 'success', confirmButtonText: t('UserManagement.okButton') });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || t('UserManagement.errorCreateUser');
      Swal.fire({ title: t('UserManagement.errorTitle'), text: String(msg), icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.userName || '').toLowerCase().includes(q)
      || (u.firstName || '').toLowerCase().includes(q)
      || (u.lastName  || '').toLowerCase().includes(q)
      || (u.email     || '').toLowerCase().includes(q)
      || (u.organizationName || '').toLowerCase().includes(q)
      || (u.orgRole || '').toLowerCase().includes(q);
  });

  const isPartnerRole = PARTNER_ORG_ROLES.includes(form.orgRole);

  return (
    <div className="um-page">
      {/* ── Header ── */}
      <div className="um-header">
        <div>
          <h2 className="um-title"><i className="fas fa-users" /> {t('UserManagement.usersTitle')}</h2>
          <p className="um-subtitle">{t('UserManagement.subtitle')}</p>
        </div>
        <button className="um-add-btn" onClick={openModal}>
          <i className="fas fa-plus" /> {t('UserManagement.addUser')}
        </button>
      </div>

      {/* ── Search ── */}
      <div className="um-search-row">
        <div className="um-search-wrap">
          <i className="fas fa-search um-search-icon" />
          <input className="um-search" placeholder={t('UserManagement.searchPlaceholder')}
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && (
            <button className="um-search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <span className="um-count">{t('UserManagement.userCount', { count: filtered.length })}</span>
      </div>

      {/* ── User Table ── */}
      <div className="um-card">
        {loading ? (
          <div className="um-loading"><div className="um-spinner" /></div>
        ) : (
          <div className="um-table-wrap">
            <table className="um-table">
              <thead>
                <tr>
                  <th>{t('UserManagement.thName')}</th>
                  <th>{t('UserManagement.thUsername')}</th>
                  <th>{t('UserManagement.thEmail')}</th>
                  <th>{t('UserManagement.thOrgRole')}</th>
                  <th>{t('UserManagement.thSystemRole')}</th>
                  <th>{t('UserManagement.thOrganization')}</th>
                  <th>{t('UserManagement.thStatus')}</th>
                  <th>{t('UserManagement.thActions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="um-empty">
                      <i className="fas fa-user-slash" />
                      <p>{search ? t('UserManagement.emptySearch') : t('UserManagement.emptyNoUsers')}</p>
                    </td>
                  </tr>
                ) : filtered.map(u => (
                  <tr key={u.id} className="um-row">
                    <td className="um-name-cell">
                      <div className="um-avatar">{(u.firstName?.[0] || '?').toUpperCase()}</div>
                      <span>{u.firstName} {u.lastName}</span>
                    </td>
                    <td className="um-mono">{u.userName}</td>
                    <td className="um-email">{u.email}</td>
                    <td><OrgRoleBadge role={u.orgRole} /></td>
                    <td><SystemRoleBadge authorities={u.authorities || []} /></td>
                    <td className="um-org">{u.organizationName}</td>
                    <td>
                      <span className={`um-status-dot ${u.isActive ? 'active' : 'inactive'}`}>
                        {u.isActive ? t('UserManagement.statusActive') : t('UserManagement.statusInactive')}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="um-btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleResetPassword(u)} disabled={resettingId === u.id}>
                          <i className="fas fa-key" /> {resettingId === u.id ? t('UserManagement.resetting') : t('UserManagement.resetPassword')}
                        </button>
                        <button className="um-btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleResetMfa(u)} disabled={mfaResettingId === u.id}
                          title={u.mfaEnabled ? t('UserManagement.mfaEnabled') : t('UserManagement.mfaNotEnabled')}>
                          <i className="fas fa-mobile-alt" /> {mfaResettingId === u.id ? t('UserManagement.resetting') : t('UserManagement.resetMfa')}
                        </button>
                        {currentUserIsPlatformAdmin() && u.isActive && (u.userName || '').toLowerCase() !== currentUserName && (
                          <button className="um-btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem', color: '#b91c1c', borderColor: '#fecaca' }}
                            onClick={() => handleDeactivate(u)} disabled={deactivatingId === u.id}
                            title={t('UserManagement.deactivateTitle')}>
                            <i className="fas fa-user-slash" /> {deactivatingId === u.id ? t('UserManagement.deactivating') : t('UserManagement.deactivate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Temp password reveal ── */}
      {resetResult && (
        <div className="um-overlay" onClick={() => setResetResult(null)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3 style={{ margin: 0 }}>{t('UserManagement.tempPasswordTitle')}</h3>
              <button className="um-modal-close" onClick={() => setResetResult(null)}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ fontSize: '0.875rem', color: '#475569' }}>
                {t('UserManagement.tempPasswordBodyBefore')}<strong>{resetResult.name}</strong>{t('UserManagement.tempPasswordBodyAfter')}
              </p>
              <code style={{ display: 'block', padding: '10px 12px', background: '#f1f5f9',
                             borderRadius: 6, fontSize: '1rem', letterSpacing: 1, userSelect: 'all' }}>
                {resetResult.tempPassword}
              </code>
              <button className="um-btn-primary" style={{ marginTop: 12 }}
                onClick={() => navigator.clipboard.writeText(resetResult.tempPassword)}>
                <i className="fas fa-copy" /> {t('UserManagement.copy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add User Modal ── */}
      {showModal && (
        <div className="um-overlay" onClick={closeModal}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <span><i className="fas fa-user-plus" /> {t('UserManagement.addUser')}</span>
              <button className="um-modal-close" onClick={closeModal}>
                <i className="fas fa-times" />
              </button>
            </div>

            <form className="um-modal-body" onSubmit={handleSubmit}>
              {/* ── Personal Details ── */}
              <div className="um-section-label">{t('UserManagement.sectionPersonalDetails')}</div>
              <div className="um-grid-2">
                <div className="um-field">
                  <label>{t('UserManagement.labelFirstName')}</label>
                  <input className={`um-input ${fieldErr.firstName ? 'um-input-err' : ''}`}
                    value={form.firstName} onChange={e => setField('firstName', e.target.value)}
                    placeholder={t('UserManagement.placeholderFirstName')} autoFocus />
                  {fieldErr.firstName && <span className="um-err">{fieldErr.firstName}</span>}
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelLastName')}</label>
                  <input className={`um-input ${fieldErr.lastName ? 'um-input-err' : ''}`}
                    value={form.lastName} onChange={e => setField('lastName', e.target.value)}
                    placeholder={t('UserManagement.placeholderLastName')} />
                  {fieldErr.lastName && <span className="um-err">{fieldErr.lastName}</span>}
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelEmail')}</label>
                  <input className={`um-input ${fieldErr.email ? 'um-input-err' : ''}`}
                    type="email" value={form.email}
                    onChange={e => setField('email', e.target.value)}
                    placeholder={t('UserManagement.placeholderEmail')} />
                  {fieldErr.email && <span className="um-err">{fieldErr.email}</span>}
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelPhoneNumber')}</label>
                  <input className="um-input" value={form.phoneNumber}
                    onChange={e => setField('phoneNumber', e.target.value)}
                    placeholder={t('UserManagement.placeholderPhoneNumber')} />
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelUsername')}</label>
                  <input className={`um-input ${fieldErr.userName ? 'um-input-err' : ''}`}
                    value={form.userName} onChange={e => setField('userName', e.target.value)}
                    placeholder={t('UserManagement.placeholderUsername')} />
                  {fieldErr.userName && <span className="um-err">{fieldErr.userName}</span>}
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelPassword')}</label>
                  <input className={`um-input ${fieldErr.password ? 'um-input-err' : ''}`}
                    type="password" value={form.password}
                    onChange={e => setField('password', e.target.value)}
                    placeholder={t('UserManagement.placeholderPassword')} />
                  {fieldErr.password && <span className="um-err">{fieldErr.password}</span>}
                </div>
              </div>

              {/* ── Access & Role ── */}
              <div className="um-section-label" style={{ marginTop: 20 }}>{t('UserManagement.sectionAccessRole')}</div>
              <div className="um-grid-2">
                <div className="um-field">
                  <label>{t('UserManagement.labelOrgRole')}</label>
                  <select className="um-input" value={form.orgRole}
                    onChange={e => setField('orgRole', e.target.value)}>
                    {ORG_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{t(`UserManagement.${r.labelKey}`)}</option>
                    ))}
                  </select>
                  {isPartnerRole && (
                    <span className="um-field-note">
                      <i className="fas fa-info-circle" /> {t('UserManagement.partnerRoleNote')}
                    </span>
                  )}
                </div>
                <div className="um-field">
                  <label>{t('UserManagement.labelSystemRole')}</label>
                  <select className="um-input" value={form.systemRole}
                    onChange={e => setField('systemRole', e.target.value)}>
                    <option value="ROLE_USER">{t('UserManagement.optionRoleUser')}</option>
                    <option value="ROLE_ADMIN">{t('UserManagement.optionRoleAdmin')}</option>
                    {/* Platform roles can only be granted by a full platform admin (backend enforced). */}
                    {canGrantPlatformRoles && (
                      <option value="ROLE_PLATFORM_USER">{t('UserManagement.optionRolePlatformUser')}</option>
                    )}
                    {canGrantPlatformRoles && (
                      <option value="ROLE_PLATFORM_ADMIN">{t('UserManagement.optionRolePlatformAdmin')}</option>
                    )}
                  </select>
                  {isPlatformAdminForm && (
                    <span className="um-field-note" style={{ color: '#7c3aed' }}>
                      <i className="fas fa-exclamation-triangle" /> {t('UserManagement.platformAdminWarning')}
                    </span>
                  )}
                  {isPlatformUserForm && (
                    <span className="um-field-note" style={{ color: '#0e7490' }}>
                      <i className="fas fa-info-circle" /> {t('UserManagement.platformUserNote')}
                    </span>
                  )}
                </div>
                <div className="um-field um-field-inline">
                  <label>{t('UserManagement.labelActive')}</label>
                  <label className="um-toggle">
                    <input type="checkbox" checked={form.isActive}
                      onChange={e => setField('isActive', e.target.checked)} />
                    <span className="um-toggle-slider" />
                  </label>
                </div>
              </div>

              {/* ── Organization ── */}
              {isPlatformStaffForm ? (
                <div className="um-field-note" style={{ marginTop: 20, padding: '10px 12px', background: '#ede9fe', borderRadius: 6, color: '#6d28d9' }}>
                  <i className="fas fa-building" /> {t('UserManagement.cataamOrgNoteBefore')}<strong>Cataam</strong>{t('UserManagement.cataamOrgNoteAfter')}
                </div>
              ) : null}
              {!isPlatformStaffForm && <div className="um-section-label" style={{ marginTop: 20 }}>{t('UserManagement.sectionOrganization')}</div>}
              {!isPlatformStaffForm && <div className="um-org-mode-row">
                <label className={`um-org-mode-opt ${form.orgMode === 'existing' ? 'selected' : ''}`}>
                  <input type="radio" name="orgMode" value="existing"
                    checked={form.orgMode === 'existing'}
                    onChange={() => setField('orgMode', 'existing')} />
                  <i className="fas fa-building" /> {t('UserManagement.selectExistingOrg')}
                </label>
                <label className={`um-org-mode-opt ${form.orgMode === 'new' ? 'selected' : ''}`}>
                  <input type="radio" name="orgMode" value="new"
                    checked={form.orgMode === 'new'}
                    onChange={() => setField('orgMode', 'new')} />
                  <i className="fas fa-plus-circle" /> {t('UserManagement.createNewOrg')}
                </label>
              </div>}

              {!isPlatformStaffForm && form.orgMode === 'existing' ? (
                <div className="um-field">
                  <label>{t('UserManagement.labelOrganization')}</label>
                  <select className={`um-input ${fieldErr.organizationId ? 'um-input-err' : ''}`}
                    value={form.organizationId}
                    onChange={e => setField('organizationId', e.target.value)}>
                    <option value="">{t('UserManagement.optionSelectOrganization')}</option>
                    {orgs.map(o => (
                      <option key={o.id} value={o.id}>{o.organizationName}</option>
                    ))}
                  </select>
                  {fieldErr.organizationId && <span className="um-err">{fieldErr.organizationId}</span>}
                </div>
              ) : !isPlatformStaffForm ? (
                <div className="um-grid-2">
                  <div className="um-field">
                    <label>{t('UserManagement.labelOrganizationName')}</label>
                    <input className={`um-input ${fieldErr.newOrgName ? 'um-input-err' : ''}`}
                      value={form.newOrgName}
                      onChange={e => setField('newOrgName', e.target.value)}
                      placeholder={t('UserManagement.placeholderOrganizationName')} />
                    {fieldErr.newOrgName && <span className="um-err">{fieldErr.newOrgName}</span>}
                  </div>
                  <div className="um-field">
                    <label>{t('UserManagement.labelLinkToPartner')} <span className="um-optional">{t('UserManagement.optionalSuffix')}</span></label>
                    <select className="um-input" value={form.partnerId}
                      onChange={e => setField('partnerId', e.target.value)}>
                      <option value="">{t('UserManagement.optionNoPartnerLink')}</option>
                      {partners.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.firmName} ({p.partnerType})
                        </option>
                      ))}
                    </select>
                    <span className="um-field-note">
                      {t('UserManagement.partnerLinkNote')}
                    </span>
                  </div>
                </div>
              ) : null}
            </form>

            <div className="um-modal-footer">
              <button className="um-btn-secondary" onClick={closeModal} type="button">{t('UserManagement.cancel')}</button>
              <button className="um-btn-primary" onClick={handleSubmit} disabled={saving} type="button">
                {saving
                  ? <><i className="fas fa-spinner fa-spin" /> {t('UserManagement.creating')}</>
                  : <><i className="fas fa-user-plus" /> {t('UserManagement.createUser')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
